import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link as RouterLink } from 'react-router-dom'
import { Alert, Box, Button, IconButton, Link, Paper, Stack, TextField, Typography } from '@mui/material'
import Close from '@mui/icons-material/Close'
import {
  aiErrorKind,
  decideTrailItem,
  requestSuggestions,
  type Suggestion,
  type SuggestionResult,
} from '../api/aiSuggestions'
import { textoParaTela, type DescriptionBlock, type ItemRequirement } from '../api/jobDescription'
import {
  addItemFromDraft,
  canAddItem,
  editItem,
  itemsOfBlock,
  linkableDeliverables,
  REQUIREMENT_BLOCKS,
  type DescriptionVersion,
  type DraftItem,
} from '../api/jobDescriptionDraft'
import { ErrorState } from './ErrorState'
import { VigaIcon } from './VigaIcon'
import { VIGA, VIGA_TOQUES_MS, prefereMenosMovimento } from './viga'
import { t } from '../i18n'

/**
 * s59 -- PAINEL DE IA ao lado do editor (referencia Attio aprovada na S31-7; ADR-0027 D3-D9).
 *
 * - NAO e modal: fica fixo a direita e o editor continua usavel enquanto a IA pensa (G2 = A, 16/10/2026: a espera
 *   de ate ~2,5 s do papel acontece aqui, com o "pensando" visivel).
 * - Cada sugestao: Aceitar (decisao approved + inclusao pela trilha), Editar e aceitar (edited + texto final),
 *   Recusar (rejected). Bloco de item unico ja preenchido (papel): "Substituir" = approved + edita o texto do item
 *   atual (o item continua o mesmo; a trilha guarda a decisao).
 * - Erros com nome (IA nao habilitada, teto do usuario, teto da empresa); qualquer outro -> ErrorState com o codigo
 *   de rastreio. "degraded" (HTTP 200) mostra o codigo de rastreio DO PEDIDO (E5-s56).
 * - s60 VIGA (marca v1.5.1): selo no cabecalho ("VIGA · IA STRONGERH"), texto de apresentacao aprovado e o "pensando":
 *   2 toques de 1,2 s (2,4 s) e PARA -- passou disso, volta ao desenho parado e fica so o progresso em texto; com
 *   "menos movimento" pedido pelo sistema, fica parado desde o inicio. Nada em loop (sai a barra indeterminada).
 * - s60 "outras B": depois de um sucesso volta o "Sugerir outras" -- o prompt 1.0.2 recebe o que ja foi sugerido.
 */

export interface AiSuggestionPanelProps {
  descriptionId: string
  block: DescriptionBlock
  items: readonly DraftItem[]
  onApplied: (v: DescriptionVersion) => void
  onClose: () => void
  /** s59 D5: a API disse que a IA nao esta habilitada para esta empresa (a pagina esconde os botoes). */
  onNotEnabled?: () => void
}

function Cartao({
  s,
  block,
  items,
  ocupado,
  onAceitar,
  onRecusar,
}: {
  s: Suggestion
  block: DescriptionBlock
  items: readonly DraftItem[]
  ocupado: boolean
  onAceitar: (textoEditado: string | null, extras: { requirement?: ItemRequirement; servesToItemId?: string }) => void
  onRecusar: () => void
}) {
  const [editando, setEditando] = useState(false)
  const [texto, setTexto] = useState(s.text)
  const [exigencia, setExigencia] = useState<ItemRequirement>('necessario')
  const [entrega, setEntrega] = useState('')
  const substitui = !canAddItem(block, items)
  const comExigencia = REQUIREMENT_BLOCKS.includes(block)
  const atividade = block === 'detalhamento'
  const entregas = linkableDeliverables([...items])

  function extras() {
    const e: { requirement?: ItemRequirement; servesToItemId?: string } = {}
    if (comExigencia) e.requirement = exigencia
    if (atividade && entrega !== '') e.servesToItemId = entrega
    return e
  }

  return (
    <Paper variant="outlined" data-testid={`sugestao-${s.trailItemId}`} sx={{ p: 1.5 }}>
      {editando ? (
        <TextField value={texto} onChange={(e) => setTexto(e.target.value)} multiline size="small" fullWidth label={t.aiPanel.edit} />
      ) : (
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
          {s.text}
        </Typography>
      )}
      {s.warnings.length > 0 && (
        <Alert severity="warning" variant="outlined" sx={{ mt: 1, py: 0 }}>
          <Typography variant="caption" component="span" sx={{ fontWeight: 600 }}>
            {t.aiPanel.methodWarning}{' '}
          </Typography>
          {s.warnings.map((w) => (
            <Typography key={w} variant="caption" component="span" display="block">
              {w}
            </Typography>
          ))}
        </Alert>
      )}
      {!substitui && (comExigencia || atividade) && (
        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {comExigencia && (
            <TextField
              select
              label={t.descriptionEditor.requirementLabel}
              value={exigencia}
              onChange={(e) => setExigencia(e.target.value as ItemRequirement)}
              size="small"
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
              sx={{ minWidth: 140 }}
            >
              <option value="necessario">{t.descriptionEditor.necessario}</option>
              <option value="desejavel">{t.descriptionEditor.desejavel}</option>
            </TextField>
          )}
          {atividade && (
            <TextField
              select
              label={t.descriptionEditor.linkLabel}
              value={entrega}
              onChange={(e) => setEntrega(e.target.value)}
              size="small"
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
              sx={{ minWidth: 0, flex: '1 1 180px' }}
            >
              <option value="">{t.descriptionEditor.transversal}</option>
              {entregas.map((e) => (
                <option key={e.id} value={e.id}>
                  {textoParaTela(e.text)}
                </option>
              ))}
            </TextField>
          )}
        </Box>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
        {editando ? (
          <Button size="small" variant="contained" disabled={ocupado || texto.trim() === ''} onClick={() => onAceitar(texto.trim(), extras())}>
            {t.aiPanel.acceptEdited}
          </Button>
        ) : (
          <>
            <Button size="small" variant="contained" disabled={ocupado} onClick={() => onAceitar(null, extras())}>
              {substitui ? t.aiPanel.replace : t.aiPanel.accept}
            </Button>
            <Button size="small" disabled={ocupado} onClick={() => setEditando(true)}>
              {t.aiPanel.edit}
            </Button>
          </>
        )}
        <Button size="small" color="inherit" disabled={ocupado} onClick={onRecusar}>
          {t.aiPanel.reject}
        </Button>
      </Stack>
    </Paper>
  )
}

export function AiSuggestionPanel({ descriptionId, block, items, onApplied, onClose, onNotEnabled }: AiSuggestionPanelProps) {
  const [resultado, setResultado] = useState<SuggestionResult | null>(null)
  const [pendentes, setPendentes] = useState<Suggestion[]>([])
  const [erroAcao, setErroAcao] = useState<unknown>(null)
  const [animando, setAnimando] = useState(false)
  const [pedidoN, setPedidoN] = useState(0)
  const relogio = useRef<ReturnType<typeof setTimeout> | null>(null)
  const titulo = t.descriptionEditor.blockTitle[block]

  // "Pensando" da Viga: anima so nos primeiros 2,4 s de cada pedido (2 toques) e volta ao parado.
  function comecarPensando() {
    if (relogio.current) clearTimeout(relogio.current)
    setPedidoN((n) => n + 1)
    if (prefereMenosMovimento()) {
      setAnimando(false)
      return
    }
    setAnimando(true)
    relogio.current = setTimeout(() => setAnimando(false), VIGA_TOQUES_MS)
  }
  useEffect(() => () => {
    if (relogio.current) clearTimeout(relogio.current)
  }, [])

  const pedir = useMutation({
    mutationFn: () => requestSuggestions(descriptionId, block),
    gcTime: 0,
    onMutate: () => comecarPensando(),
    onSuccess: (r) => {
      setResultado(r)
      setPendentes(r.suggestions)
    },
    onError: (e) => {
      if (aiErrorKind(e) === 'not_enabled') onNotEnabled?.()
    },
  })

  const decidir = useMutation({
    mutationFn: async (a: { s: Suggestion; tipo: 'aceitar' | 'recusar'; texto: string | null; extras: { requirement?: ItemRequirement; servesToItemId?: string } }) => {
      const roundId = resultado?.roundId ?? ''
      if (a.tipo === 'recusar') {
        await decideTrailItem(roundId, a.s.trailItemId, 'rejected')
        return null
      }
      const final = a.texto ?? a.s.text
      const editado = a.texto !== null && a.texto !== a.s.text
      await decideTrailItem(roundId, a.s.trailItemId, editado ? 'edited' : 'approved', editado ? final : undefined)
      if (!canAddItem(block, items)) {
        const atual = itemsOfBlock([...items], block)[0]
        return editItem(descriptionId, atual.id, { text: final })
      }
      return addItemFromDraft(descriptionId, { roundId, draftItemId: a.s.trailItemId, ...a.extras })
    },
    onSuccess: (v, a) => {
      setErroAcao(null)
      setPendentes((ps) => ps.filter((p) => p.trailItemId !== a.s.trailItemId))
      if (v) onApplied(v)
    },
    onError: setErroAcao,
  })

  // Pede ao abrir (e de novo quando o bloco muda): a pessoa clicou "Sugerir com IA" -- e o disparo explicito (D3).
  const { mutate: dispararPedido } = pedir
  useEffect(() => {
    setResultado(null)
    setPendentes([])
    setErroAcao(null)
    dispararPedido()
  }, [block, descriptionId, dispararPedido])

  const tipoErro = pedir.isError ? aiErrorKind(pedir.error) : null

  return (
    <Paper
      component="aside"
      elevation={6}
      data-testid="painel-ia"
      aria-label={t.aiPanel.title(titulo)}
      sx={{
        position: 'fixed',
        right: 0,
        top: 0,
        height: '100vh',
        width: { xs: '100vw', sm: 400 },
        zIndex: (th) => th.zIndex.drawer,
        overflowY: 'auto',
        p: 2,
        borderRadius: 0,
      }}
    >
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* s60: selo do kit v1.1 (marca v1.5.1) -- a empunhadura branca do v1.0, que "parecia um cigarro" no fundo
              azul-escuro (Marcelo, 18/10/2026 20:25), virou azul-clara na Fabrica da Marca; o selo voltou ao cabecalho. */}
          <VigaIcon
            src={pedir.isPending && animando ? `${VIGA.seloPensando}?p=${pedidoN}` : VIGA.selo}
            size={48}
            testId={pedir.isPending && animando ? 'viga-selo-pensando' : 'viga-selo'}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ fontWeight: 600 }}>
              {t.aiPanel.vigaBadge}
            </Typography>
            <Typography variant="h2" sx={{ fontSize: '1.0625rem' }}>
              {t.aiPanel.title(titulo)}
            </Typography>
          </Box>
          <IconButton size="small" aria-label={t.aiPanel.close} onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {t.aiPanel.vigaIntro}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t.aiPanel.disclaimer}{' '}
          <Link component={RouterLink} to="/ia-e-seus-dados">
            {t.aiPanel.dataLink}
          </Link>
        </Typography>

        {pedir.isPending && (
          <Box data-testid="ia-pensando" role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VigaIcon
              src={animando ? `${VIGA.iconePensando}?p=${pedidoN}` : VIGA.icone}
              size={24}
              testId={animando ? 'viga-pensando' : 'viga-parada'}
            />
            <Typography variant="body2" color="text.secondary">
              {t.aiPanel.thinking}
            </Typography>
          </Box>
        )}

        {tipoErro === 'not_enabled' && <Alert severity="info">{t.aiPanel.notEnabled}</Alert>}
        {tipoErro === 'user_budget' && <Alert severity="warning">{t.aiPanel.userBudget}</Alert>}
        {tipoErro === 'tenant_budget' && <Alert severity="warning">{t.aiPanel.tenantBudget}</Alert>}
        {tipoErro === 'other' && <ErrorState error={pedir.error} onRetry={() => pedir.mutate()} />}

        {resultado?.status === 'degraded' && (
          <Alert severity="warning">
            <Typography variant="body2">{t.aiPanel.degraded}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {t.error.requestIdHint}
            </Typography>
            <Typography variant="caption" component="code" sx={{ fontFamily: 'monospace' }}>
              {resultado.requestId}
            </Typography>
          </Alert>
        )}

        {resultado !== null && resultado.redactedCount > 0 && (
          <Alert severity="info" variant="outlined">
            {t.aiPanel.redacted(resultado.redactedCount)}
          </Alert>
        )}

        {erroAcao !== null && <ErrorState error={erroAcao} />}

        {pendentes.map((s) => (
          <Cartao
            key={s.trailItemId}
            s={s}
            block={block}
            items={items}
            ocupado={decidir.isPending}
            onAceitar={(texto, extras) => decidir.mutate({ s, tipo: 'aceitar', texto, extras })}
            onRecusar={() => decidir.mutate({ s, tipo: 'recusar', texto: null, extras: {} })}
          />
        ))}

        {resultado !== null && resultado.status !== 'degraded' && pendentes.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t.aiPanel.none}
          </Typography>
        )}

        {/* Apos FALHA: "Tentar de novo". s60 "outras B": apos SUCESSO volta "Sugerir outras" -- o prompt 1.0.2 recebe o
            que ja foi sugerido para este cargo e bloco e traz ideias novas (a s59 escondia o botao porque o pedido
            repetido devolvia as mesmas frases). */}
        {!pedir.isPending && (resultado?.status === 'degraded' || tipoErro === 'other') && (
          <Box>
            <Button size="small" variant="outlined" startIcon={<VigaIcon src={VIGA.icone} size={20} />} onClick={() => pedir.mutate()}>
              {t.aiPanel.retry}
            </Button>
          </Box>
        )}
        {!pedir.isPending && resultado !== null && resultado.status !== 'degraded' && (
          <Box>
            <Button size="small" variant="outlined" startIcon={<VigaIcon src={VIGA.icone} size={20} />} onClick={() => pedir.mutate()}>
              {t.aiPanel.suggestMore}
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  )
}
